import { IonIcon } from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { chevronForwardOutline } from 'ionicons/icons'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { waypointAddress } from '../../helpers/waypointAddress'
import type { WaypointTime } from '../../helpers/waypointTimes'
import { WaypointSchedule } from './WaypointSchedule'
import {
  type TimelineDate,
  type TimelineWaypoint,
  formatWaypointAddress,
} from './types'

/**
 * Card frame shared by every stop: linked (chevron, press feedback) when
 * `linkTo` is set, static otherwise (yard stops, cancelled assignment).
 */
const StopCardFrame = ({
  linkTo,
  children,
}: {
  linkTo?: string
  children: ReactNode
}) => {
  if (!linkTo) {
    return (
      <div className='route-timeline__card-link'>
        <div className='route-timeline__card route-timeline__card--static'>
          {children}
        </div>
      </div>
    )
  }
  return (
    <Link to={linkTo} className='no-style route-timeline__card-link'>
      <div className='route-timeline__card'>
        {children}
        <div className='route-timeline__chevron'>
          <IonIcon icon={chevronForwardOutline} />
        </div>
      </div>
    </Link>
  )
}

/**
 * Order stop card, links to the order detail (v1 WaypointCard). Without
 * `linkTo` (cancelled assignment) the card is plain: no link, no chevron.
 */
export const WaypointCard = ({
  waypoint,
  linkTo,
  arrival,
  departure,
  datetime,
}: {
  waypoint: TimelineWaypoint
  linkTo?: string
  arrival?: WaypointTime
  departure?: WaypointTime
  datetime: TimelineDate
}) => {
  const { _ } = useLingui()
  const order = waypoint.order
  if (!order) return null

  // v1: customer nickname (its abbreviation), else the customer name
  const customerLabel = order.customer?.nickname || order.customer?.name
  const titleParts = [customerLabel, waypoint.name].filter(Boolean)
  const orderTitle =
    titleParts.length > 0 ? titleParts.join(' | ') : _(msg`Order #${order.id}`)
  const address = waypointAddress(waypoint)

  return (
    <StopCardFrame linkTo={linkTo}>
      <div className='route-timeline__card-body'>
        <div className='route-timeline__site-name'>{orderTitle}</div>
        {address && (
          <div className='route-timeline__address'>
            {formatWaypointAddress(address)}
          </div>
        )}
        <WaypointSchedule
          arrival={arrival}
          departure={departure}
          datetime={datetime}
        />
      </div>
    </StopCardFrame>
  )
}

/** Yard/HQ stop card (departure or return), never linked. */
export const YardWaypointCard = ({
  waypoint,
  isFirst,
  isLast,
  arrival,
  departure,
  datetime,
}: {
  waypoint: TimelineWaypoint
  isFirst: boolean
  isLast: boolean
  arrival?: WaypointTime
  departure?: WaypointTime
  datetime: TimelineDate
}) => {
  const { _ } = useLingui()
  const addressLabel = waypoint.address
    ? formatWaypointAddress(waypoint.address)
    : _(msg`Yard`)

  return (
    <StopCardFrame>
      <div className='route-timeline__card-body'>
        <div className='route-timeline__site-name'>
          {waypoint.name ?? (isFirst ? _(msg`Departure`) : _(msg`Return`))}
        </div>
        {addressLabel && (
          <div className='route-timeline__address'>{addressLabel}</div>
        )}
        <WaypointSchedule
          arrival={isLast ? arrival : undefined}
          departure={isFirst ? departure : undefined}
          datetime={datetime}
        />
      </div>
    </StopCardFrame>
  )
}
